import { useSyncExternalStore } from 'react'
import {
  UI_ENERGY_STORAGE_KEY,
  energyFromRestDoc,
  parseEnergyMode,
  readUrlEnergyMode,
  resolveEnergyActive,
  type EnergyEnv,
  type EnergyMode,
} from './uiThemeCore'

/**
 * โหมดประหยัดพลังงานฝั่งลูกค้า — ตั้งค่า <html data-energy="on"> ให้ CSS/คอมโพเนนต์ปิดเอฟเฟกต์
 * ค่าที่ใช้: ?energy= > Firestore (แอดมินตั้ง · อ่านผ่าน REST 1 ครั้ง/session) > localStorage > auto
 * ไม่รอ Firestore ก่อนวาดหน้า: inline script ใน index.html ใส่ค่าจาก localStorage ไว้แล้ว
 */

const EVENT = 'oc-energy-change'
const SESSION_KEY = 'oc-energy-checked'
const UI_DOC_REST_URL =
  'https://firestore.googleapis.com/v1/projects/srikanett-order/databases/(default)/documents/publicIntegrations/ordercusUi'

let mode: EnergyMode = 'auto'
let batteryLow = false
let started = false

function readEnv(): EnergyEnv {
  const nav = navigator as Navigator & {
    deviceMemory?: number
    connection?: { saveData?: boolean }
  }
  const cores = typeof nav.hardwareConcurrency === 'number' ? nav.hardwareConcurrency : 0
  const memory = typeof nav.deviceMemory === 'number' ? nav.deviceMemory : 0
  let reducedMotion = false
  try {
    reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  } catch {
    /* ไม่รองรับ matchMedia */
  }
  return {
    reducedMotion,
    saveData: !!nav.connection?.saveData,
    lowHardware: (cores > 0 && cores <= 4) || (memory > 0 && memory <= 4),
    lowBattery: batteryLow,
  }
}

export function isEnergySaving(): boolean {
  return document.documentElement.dataset.energy === 'on'
}

function apply(): void {
  const active = resolveEnergyActive(mode, readEnv())
  const root = document.documentElement
  if (active === (root.dataset.energy === 'on')) return
  if (active) root.dataset.energy = 'on'
  else delete root.dataset.energy
  window.dispatchEvent(new CustomEvent(EVENT, { detail: { active } }))
}

function setMode(next: EnergyMode): void {
  mode = next
  apply()
}

/** เริ่มครั้งเดียวใน main.tsx */
export function startEnergyModeSync(): void {
  if (started) return
  started = true
  const urlMode = readUrlEnergyMode(window.location.search)
  let stored: EnergyMode | null = null
  try {
    stored = parseEnergyMode(localStorage.getItem(UI_ENERGY_STORAGE_KEY))
  } catch {
    /* private mode */
  }
  mode = urlMode ?? stored ?? 'auto'
  apply()

  // แบตต่ำ (เฉพาะโหมด auto มีผล) — ไม่รองรับ getBattery (Safari/LINE บางตัว) = ข้าม
  const nav = navigator as Navigator & {
    getBattery?: () => Promise<{ level: number; charging: boolean; addEventListener: (t: string, l: () => void) => void }>
  }
  nav.getBattery?.().then((battery) => {
    const sync = () => {
      batteryLow = battery.level < 0.22 && !battery.charging
      apply()
    }
    sync()
    battery.addEventListener('levelchange', sync)
    battery.addEventListener('chargingchange', sync)
  }).catch(() => {
    /* ไม่รองรับ */
  })

  try {
    window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', apply)
  } catch {
    /* ไม่รองรับ */
  }

  if (urlMode) return
  try {
    if (sessionStorage.getItem(SESSION_KEY)) return
  } catch {
    /* private mode — เช็คทุกครั้งที่เปิดหน้า */
  }
  const controller = typeof AbortController === 'undefined' ? null : new AbortController()
  const timer = window.setTimeout(() => controller?.abort(), 6000)
  fetch(UI_DOC_REST_URL, { signal: controller?.signal, cache: 'no-store' })
    .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
    .then((doc) => {
      const remote = energyFromRestDoc(doc)
      try {
        localStorage.setItem(UI_ENERGY_STORAGE_KEY, remote)
        sessionStorage.setItem(SESSION_KEY, '1')
      } catch {
        /* private mode / quota */
      }
      setMode(remote)
    })
    .catch(() => {
      /* ออฟไลน์/ช้า — คงค่าเดิม */
    })
    .finally(() => window.clearTimeout(timer))
}

function subscribe(listener: () => void) {
  window.addEventListener(EVENT, listener)
  return () => window.removeEventListener(EVENT, listener)
}

/** ให้คอมโพเนนต์ (วิดีโอ/แอนิเมชันที่คุมด้วย JS) เปลี่ยนตามทันที */
export function useEnergySaving(): boolean {
  return useSyncExternalStore(subscribe, isEnergySaving, () => false)
}
